import { useNavigate, useLocation } from 'react-router-dom';
import ProductCardRow from './card/ProductImageCardRow';
import Filter from '../../form/Filter';
import { useEffect, useRef, useCallback, useState } from 'react';
import { useProductByFranchiseStore } from '../../../store/useProduct';
import { useLoginStore } from '../../../store/useLogin';
import { useFranchiseStore } from '../../../store/useFranchise';
import queryString from 'query-string';
import { updateUrlWithParams } from '../../../util/util';
import { toast } from 'react-toastify';

/**
 * @author jinhee
 * @createDate 2025.07.17
 * @description 상품 확인 페이지
 */
const ProductThumbnail = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { role } = useLoginStore();
    const { products, getProducts, clearProducts } = useProductByFranchiseStore();
    const { franchises, getFranchise } = useFranchiseStore();

    const fieldSelectRef = useRef<HTMLSelectElement>(null);
    const orderSelectRef = useRef<HTMLSelectElement>(null);
    const searchTextRef = useRef<HTMLInputElement>(null);
    
    const [selectedFranchiseId, setSelectedFranchiseId] = useState<string>('');
    const [showFranchiseDropdown, setShowFranchiseDropdown] = useState<boolean>(false);
    const [franchiseSearchText, setFranchiseSearchText] = useState<string>('');

    const fieldOptions = [{ option: '회사명', value: 'franchiseName' }];

    const handleSearch = useCallback((franchiseIdParam?: string) => {
        const searchText = searchTextRef.current?.value || '';
        const order = orderSelectRef.current?.value || 'desc';
        
        // 파라미터로 전달된 franchiseId 또는 state의 selectedFranchiseId 사용
        const franchiseIdToUse = franchiseIdParam || selectedFranchiseId;
        
        // 회사명 검색이거나 회사 선택이 있을 때만 진행
        if (!franchiseIdToUse && !searchText) {
            return;
        }

        updateUrlWithParams({
            franchiseId: franchiseIdToUse || undefined,
            order: order !== 'desc' ? order : undefined,
            search: searchText || undefined
        }, navigate);

        getProducts(franchiseIdToUse, order as 'asc' | 'desc');
    }, [navigate, getProducts, selectedFranchiseId, franchises]);

    useEffect(() => {
        const parsedQuery = queryString.parse(location.search);
        const search = (parsedQuery.search as string) || '';
        const franchiseId = (parsedQuery.franchiseId as string) || '';
        const field = (parsedQuery.field as string) || 'franchiseName';
        const order = (parsedQuery.order as 'asc' | 'desc') || 'desc';

        // Update input values
        if (searchTextRef.current) searchTextRef.current.value = search;
        if (fieldSelectRef.current) fieldSelectRef.current.value = field;
        if (orderSelectRef.current) orderSelectRef.current.value = order;
        if (franchiseId) setSelectedFranchiseId(franchiseId);
        
        // 검색어와 회사 선택 둘 중 하나라도 있을 때만 API 호출
        if (franchiseId || search) {
            getProducts(franchiseId, order);
        }
    }, [location.search, getProducts]);
    
    // 회사 목록 가져오기
    useEffect(() => {
        getFranchise({ size: 1000 });
    }, [getFranchise]);
    
    // 드롭다운 외부 클릭시 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.franchise-dropdown-container')) {
                setShowFranchiseDropdown(false);
                setFranchiseSearchText('');
            }
        };
        
        if (showFranchiseDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFranchiseDropdown]);

    // 컴포넌트 언마운트 시 상품 목록 초기화
    useEffect(() => {
        return () => {
            clearProducts();
        };
    }, [clearProducts]);

    // 스켈레톤 데이터
    const skeletonData = Array.from({ length: 8 }, (_, i) => ({
        id: `skeleton${i + 1}`,
        skeleton: true,
    }));

    return (
        <div className="!w-full !min-h-full !flex !flex-col">
            <div className="!flex !justify-end !items-center !pt-4 !pb-4 !border-b !border-gray-200 !mb-4">
                {/* 회사 선택 토글 */}
                <div className="!relative franchise-dropdown-container">
                    <button
                        onClick={() => setShowFranchiseDropdown(!showFranchiseDropdown)}
                        className="!flex !items-center !gap-2 !px-4 !py-2 !bg-white !border !border-gray-300 !rounded-lg !text-sm !font-medium !text-gray-700 hover:!bg-gray-50 !transition-colors"
                    >
                        <span>{selectedFranchiseId ? franchises?.find(f => f.id === selectedFranchiseId)?.companyName || '회사 선택' : '회사 선택'}</span>
                        <svg className="!w-4 !h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    
                    {showFranchiseDropdown && (
                        <div className="!absolute !right-0 !mt-2 !w-64 !bg-white !border !border-gray-200 !rounded-lg !shadow-lg !z-10">
                            {/* 검색 입력창 */}
                            <div className="!p-3 !border-b !border-gray-200">
                                <div className="!relative">
                                    <input
                                        type="text"
                                        value={franchiseSearchText}
                                        onChange={(e) => setFranchiseSearchText(e.target.value)}
                                        placeholder="회사명으로 검색..."
                                        className="!w-full !pl-8 !pr-3 !py-2 !text-sm !border !border-gray-300 !rounded-md focus:!outline-none focus:!ring-2 focus:!ring-blue-500 focus:!border-transparent"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <svg className="!absolute !left-2.5 !top-1/2 !transform !-translate-y-1/2 !w-4 !h-4 !text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                            </div>
                            
                            <div className="!max-h-60 !overflow-y-auto">
                                {franchises && franchises.length > 0 ? (
                                    franchises
                                        .filter(franchise => {
                                            // 검색어가 없으면 모든 항목 표시
                                            if (!franchiseSearchText) return true;
                                            // 검색어가 있으면 companyName이 있는 것만 검색
                                            return franchise.companyName && franchise.companyName.toLowerCase().includes(franchiseSearchText.toLowerCase());
                                        })
                                        .map((franchise) => (
                                            <button
                                                key={franchise.id}
                                                onClick={() => {
                                                    setSelectedFranchiseId(franchise.id);
                                                    setShowFranchiseDropdown(false);
                                                    setFranchiseSearchText('');
                                                    // 회사를 선택하면 검색 실행
                                                    if (searchTextRef.current) searchTextRef.current.value = '';
                                                    // 선택한 franchiseId를 직접 전달
                                                    handleSearch(franchise.id);
                                                }}
                                                className="!w-full !px-4 !py-2 !text-left !text-sm !text-gray-700 hover:!bg-gray-100 !transition-colors"
                                            >
                                                <div className="!font-medium">{franchise.companyName || ''}</div>
                                                <div className="!text-xs !text-gray-500">{franchise.ceo || '-'} | {franchise.telNo || '-'}</div>
                                            </button>
                                        ))
                                ) : (
                                    <div className="!px-4 !py-3 !text-sm !text-gray-500">회사 목록이 없습니다.</div>
                                )}
                                {franchises && franchises.filter(franchise => {
                                    if (!franchiseSearchText) return true;
                                    return franchise.companyName && franchise.companyName.toLowerCase().includes(franchiseSearchText.toLowerCase());
                                }).length === 0 && franchiseSearchText && (
                                    <div className="!px-4 !py-3 !text-sm !text-gray-500">검색 결과가 없습니다.</div>
                                )}
                            </div>
                            <div className="!border-t !border-gray-200 !px-4 !py-2">
                                <button
                                    onClick={() => {
                                        setSelectedFranchiseId('');
                                        setShowFranchiseDropdown(false);
                                        setFranchiseSearchText('');
                                    }}
                                    className="!text-sm !text-red-600 hover:!text-red-700"
                                >
                                    선택 취소
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 초기 진입 시 입력값이 없을 때 안내 메시지 */}
            {!products && !selectedFranchiseId && !(searchTextRef.current?.value) && (
                <div className="!flex !flex-col !items-center !justify-center !flex-1 !py-20">
                    <div className="!text-center !max-w-md !mx-auto">
                        <div className="!mb-6">
                            <svg className="!w-16 !h-16 !mx-auto !text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <h3 className="!text-lg !font-medium !text-gray-900 !mb-2">
                            상품을 검색해보세요
                        </h3>
                        <p className="!text-gray-500 !text-sm !mb-6">
                            회사를 선택하여 상품을 확인할 수 있습니다.
                        </p>
                        <div className="!flex !flex-col !gap-2 !text-sm !text-gray-600">
                            <div className="!flex !items-center !gap-2">
                                <span className="!w-4 !h-4 !rounded-full !bg-blue-100 !text-blue-600 !flex !items-center !justify-center !text-xs !font-medium">1</span>
                                <span>오른쪽 상단의 '회사 선택' 버튼을 클릭하세요</span>
                            </div>
                            <div className="!flex !items-center !gap-2">
                                <span className="!w-4 !h-4 !rounded-full !bg-blue-100 !text-blue-600 !flex !items-center !justify-center !text-xs !font-medium">2</span>
                                <span>회사명으로 검색하거나 목록에서 선택하세요</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 검색 중일 때 스켈레톤 */}
            {!products && (selectedFranchiseId || searchTextRef.current?.value) && (
                <div className="!w-full !grid !grid-cols-[repeat(auto-fill,minmax(240px,1fr))] !gap-5 !pb-10 !flex-1 !content-start">
                    <ProductCardRow products={skeletonData} />
                </div>
            )}

            {/* 가져와서 있으면 */}
            {products && products.length > 0 && (
                <div className="!w-full !grid !grid-cols-[repeat(auto-fill,minmax(240px,1fr))] !gap-5 !pb-10 !flex-1 !content-start">
                    <ProductCardRow products={products} />
                </div>
            )}

            {/* 가져왔는데 없으면 */}
            {products && products.length === 0 && (
                <div
                    className="!w-full !max-w-[600px] !h-[400px] !bg-no-repeat !bg-contain !bg-center !bg-white !rounded-2xl !shadow-lg !mx-auto !my-[60px]"
                    style={{
                        backgroundImage: `url(${require('../../../assets/img/saju_no_data.png')})`,
                    }}
                ></div>
            )}
        </div>
    );
}
export default ProductThumbnail;